/**
 * QuickTradeSettingsModal Component
 *
 * Gear modal for the Trades pane — two tabs:
 *   - Quick Trades: showQuickTrades toggle + preset CRUD (add / edit / delete / reorder)
 *   - Display:      activeTradesDefaultSort radio + activeTradesCardInfo field toggles
 *
 * All edits are local until the user presses Save, which calls onSave with the
 * full updated settings block. Modal state resets to initialSettings on re-open.
 *
 * Preset form is an inline expansion below the preset list. Supports add and edit.
 * Strategy → Playbook cascade; criteria / confluence / entry-model checkboxes shown
 * only when a playbook is selected.
 *
 * `openPresetForm` prop automatically opens the Add Preset form on modal mount
 * (used by the "Configure a Quick Trade" empty-state button in TradesPane).
 *
 * @see TradesPane — opens this modal via gear icon; passes settings + handleSaveSettings
 * @see LiveTradingSettingsService.updateQuickTradesSettings — persistence
 * @see LiveTradingPhase — loads strategy/playbook reference data passed as props
 */

// 1. External dependencies
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ChevronUp, ChevronDown, Pencil, Trash2 } from 'lucide-react';

// 2. Internal absolute imports
import {
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, Input, Select, SelectItem,
  Switch, Checkbox, Divider,
} from '@enfomentis/ui';
import { generateUUID } from '@/utils';
import { useIsDesktop } from '@/hooks/useIsDesktop';

// 3. Type imports
import type { Confluence } from '@/types/schema/Confluence';
import type { EntryModel } from '@/types/schema/EntryModel';
import type { Playbook } from '@/types/schema/Playbook';
import type { PlaybookCriteriaItem } from '@/types/schema/PlaybookCriteriaItem';
import type { Strategy } from '@/types/schema/Strategy';
import type { QuickTradePreset, LiveTradingSettings } from '@/types/schema/LiveTradingSettings';

// ===== TYPES =====

type SortOption = LiveTradingSettings['activeTradesDefaultSort'];
type CardInfoField = LiveTradingSettings['activeTradesCardInfo'][number]['field'];
type CardInfoEntry = LiveTradingSettings['activeTradesCardInfo'][number];
type ActiveTab = 'quick-trades' | 'display';

export interface QuickTradeSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Initial settings to populate the modal on open */
  initialShowQuickTrades: boolean;
  initialQuickTradePresets: QuickTradePreset[];
  initialActiveTradesDefaultSort: SortOption;
  initialActiveTradesCardInfo: CardInfoEntry[];
  /** Called with the full updated settings when user presses Save */
  onSave: (updates: {
    showQuickTrades: boolean;
    quickTradePresets: QuickTradePreset[];
    activeTradesDefaultSort: SortOption;
    activeTradesCardInfo: CardInfoEntry[];
  }) => Promise<void>;
  /** When true, auto-open the Add Preset form on modal open */
  openPresetForm?: boolean;
  /** Active strategies for preset form dropdowns */
  activeStrategies?: Strategy[];
  /** Active playbooks for preset form dropdowns */
  activePlaybooks?: Playbook[];
  /** Active criteria for preset form checkboxes */
  activeCriteria?: PlaybookCriteriaItem[];
  /** Active confluences for preset form checkboxes */
  activeConfluences?: Confluence[];
  /** Active entry models for preset form checkboxes */
  activeEntryModels?: EntryModel[];
}

// ===== CONSTANTS =====

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'alphabetical', label: 'Alphabetical' },
];

const CARD_INFO_FIELDS: { field: CardInfoField; label: string }[] = [
  { field: 'playbook', label: 'Playbook' },
  { field: 'criteria', label: 'Criteria score' },
  { field: 'progress', label: 'Progress' },
  { field: 'emotions', label: 'Emotions' },
  { field: 'modified', label: 'Last modified' },
];

/** Empty preset form state */
const EMPTY_FORM = {
  name: '',
  strategyId: '',
  playbookId: '',
  checkedCriteriaIds: [] as string[],
  checkedConfluenceIds: [] as string[],
  selectedEntryModelIds: [] as string[],
};

// ===== COMPONENT =====

export const QuickTradeSettingsModal: React.FC<QuickTradeSettingsModalProps> = ({
  isOpen,
  onClose,
  initialShowQuickTrades,
  initialQuickTradePresets,
  initialActiveTradesDefaultSort,
  initialActiveTradesCardInfo,
  onSave,
  openPresetForm = false,
  activeStrategies = [],
  activePlaybooks = [],
  activeCriteria = [],
  activeConfluences = [],
  activeEntryModels = [],
}) => {
  const isDesktop = useIsDesktop();

  // ---------------------------------------------------------------------------
  // Tab state
  // ---------------------------------------------------------------------------

  const [activeTab, setActiveTab] = useState<ActiveTab>('quick-trades');

  // ---------------------------------------------------------------------------
  // Quick Trades tab state
  // ---------------------------------------------------------------------------

  const [showQuickTrades, setShowQuickTrades] = useState(initialShowQuickTrades ?? false);
  const [presets, setPresets] = useState<QuickTradePreset[]>(initialQuickTradePresets ?? []);

  // ---------------------------------------------------------------------------
  // Display tab state
  // ---------------------------------------------------------------------------

  const [activeSort, setActiveSort] = useState<SortOption>(initialActiveTradesDefaultSort ?? 'newest');
  const [cardInfo, setCardInfo] = useState<CardInfoEntry[]>(initialActiveTradesCardInfo ?? []);

  // ---------------------------------------------------------------------------
  // Preset form state
  // ---------------------------------------------------------------------------

  const [isFormOpen, setIsFormOpen] = useState(false);
  /** ID of preset being edited; undefined when adding a new preset */
  const [editingPresetId, setEditingPresetId] = useState<string | undefined>(undefined);
  const [formName, setFormName] = useState(EMPTY_FORM.name);
  const [presetNameError, setPresetNameError] = useState<string | null>(null);
  const [formStrategyId, setFormStrategyId] = useState(EMPTY_FORM.strategyId);
  const [formPlaybookId, setFormPlaybookId] = useState(EMPTY_FORM.playbookId);
  const [formCheckedCriteriaIds, setFormCheckedCriteriaIds] = useState<string[]>(EMPTY_FORM.checkedCriteriaIds);
  const [formCheckedConfluenceIds, setFormCheckedConfluenceIds] = useState<string[]>(EMPTY_FORM.checkedConfluenceIds);
  const [formSelectedEntryModelIds, setFormSelectedEntryModelIds] = useState<string[]>(EMPTY_FORM.selectedEntryModelIds);

  // ---------------------------------------------------------------------------
  // Save state
  // ---------------------------------------------------------------------------

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Reset on open (prev-ref pattern)
  // ---------------------------------------------------------------------------

  const prevIsOpenRef = useRef(isOpen);

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (!wasOpen && isOpen) {
      // Reset all state to initial values
      setActiveTab('quick-trades');
      setShowQuickTrades(initialShowQuickTrades ?? false);
      setPresets(initialQuickTradePresets ?? []);
      setActiveSort(initialActiveTradesDefaultSort ?? 'newest');
      setCardInfo(initialActiveTradesCardInfo ?? []);
      setEditingPresetId(undefined);
      setFormName(EMPTY_FORM.name);
      setPresetNameError(null);
      setFormStrategyId(EMPTY_FORM.strategyId);
      setFormPlaybookId(EMPTY_FORM.playbookId);
      setFormCheckedCriteriaIds([]);
      setFormCheckedConfluenceIds([]);
      setFormSelectedEntryModelIds([]);
      setIsFormOpen(openPresetForm);
      setIsSaving(false);
      setSaveError(null);
    }
  }, [isOpen, initialShowQuickTrades, initialQuickTradePresets, initialActiveTradesDefaultSort, initialActiveTradesCardInfo, openPresetForm]);

  // ---------------------------------------------------------------------------
  // Derived: form data for dropdowns
  // ---------------------------------------------------------------------------

  const availablePlaybooks = formStrategyId
    ? activePlaybooks.filter((p) => {
        const strategy = activeStrategies.find((s) => s.id === formStrategyId);
        return strategy?.playbookIds.includes(p.id) ?? false;
      })
    : [];
  const selectedFormPlaybook = formPlaybookId ? activePlaybooks.find((p) => p.id === formPlaybookId) : undefined;
  const formCriteria = selectedFormPlaybook?.criteriaIds
    ? activeCriteria.filter((c) => selectedFormPlaybook.criteriaIds?.includes(c.id))
    : [];
  const formConfluences = selectedFormPlaybook?.confluences
    ? selectedFormPlaybook.confluences
        .map((link) => activeConfluences.find((c) => c.id === link.confluenceId))
        .filter((c): c is Confluence => c !== undefined)
    : [];
  const formEntryModels = selectedFormPlaybook
    ? activeEntryModels.filter((em) => selectedFormPlaybook.entryModelIds.includes(em.id))
    : [];

  // ---------------------------------------------------------------------------
  // Handlers — preset form
  // ---------------------------------------------------------------------------

  const openAddForm = useCallback((): void => {
    setEditingPresetId(undefined);
    setFormName(EMPTY_FORM.name);
    setPresetNameError(null);
    setFormStrategyId(EMPTY_FORM.strategyId);
    setFormPlaybookId(EMPTY_FORM.playbookId);
    setFormCheckedCriteriaIds([]);
    setFormCheckedConfluenceIds([]);
    setFormSelectedEntryModelIds([]);
    setIsFormOpen(true);
  }, []);

  const openEditForm = useCallback((preset: QuickTradePreset): void => {
    setEditingPresetId(preset.id);
    setFormName(preset.name);
    setPresetNameError(null);
    setFormStrategyId(preset.strategyId ?? '');
    setFormPlaybookId(preset.playbookId ?? '');
    setFormCheckedCriteriaIds(preset.checkedCriteriaIds ?? []);
    setFormCheckedConfluenceIds(preset.checkedConfluenceIds ?? []);
    setFormSelectedEntryModelIds(preset.selectedEntryModelIds ?? []);
    setIsFormOpen(true);
  }, []);

  const closeForm = useCallback((): void => {
    setIsFormOpen(false);
    setEditingPresetId(undefined);
    setPresetNameError(null);
  }, []);

  const handleFormStrategyChange = useCallback((newStrategyId: string): void => {
    setFormStrategyId(newStrategyId);
    setFormPlaybookId('');
    setFormCheckedCriteriaIds([]);
    setFormCheckedConfluenceIds([]);
    setFormSelectedEntryModelIds([]);
  }, []);

  const handleFormPlaybookChange = useCallback((newPlaybookId: string): void => {
    setFormPlaybookId(newPlaybookId);
    setFormCheckedCriteriaIds([]);
    setFormCheckedConfluenceIds([]);
    setFormSelectedEntryModelIds([]);
  }, []);

  const handleSavePreset = useCallback((): void => {
    if (!formName.trim()) return;

    // Fix 4: duplicate name check (case-insensitive, excluding the preset being edited)
    const isDuplicate = presets.some(
      (p) => p.name.trim().toLowerCase() === formName.trim().toLowerCase() && p.id !== editingPresetId,
    );
    if (isDuplicate) {
      setPresetNameError('A preset with this name already exists.');
      return;
    }
    setPresetNameError(null);

    if (editingPresetId !== undefined) {
      // Update existing preset — rebuild from scratch (keep only id + order from existing)
      // so that clearing optional fields (e.g. changing strategy resets playbook) is reflected.
      setPresets((prev) =>
        prev.map((p) =>
          p.id === editingPresetId
            ? {
                id: p.id,
                name: formName.trim(),
                order: p.order,
                ...(formStrategyId ? { strategyId: formStrategyId } : {}),
                ...(formPlaybookId ? { playbookId: formPlaybookId } : {}),
                ...(formCheckedCriteriaIds.length > 0 ? { checkedCriteriaIds: formCheckedCriteriaIds } : {}),
                ...(formCheckedConfluenceIds.length > 0 ? { checkedConfluenceIds: formCheckedConfluenceIds } : {}),
                ...(formSelectedEntryModelIds.length > 0 ? { selectedEntryModelIds: formSelectedEntryModelIds } : {}),
              }
            : p,
        ),
      );
    } else {
      // Add new preset
      const maxOrder = presets.reduce((max, p) => Math.max(max, p.order), -1);
      const newPreset: QuickTradePreset = {
        id: generateUUID(),
        name: formName.trim(),
        order: maxOrder + 1,
        ...(formStrategyId ? { strategyId: formStrategyId } : {}),
        ...(formPlaybookId ? { playbookId: formPlaybookId } : {}),
        ...(formCheckedCriteriaIds.length > 0 ? { checkedCriteriaIds: formCheckedCriteriaIds } : {}),
        ...(formCheckedConfluenceIds.length > 0 ? { checkedConfluenceIds: formCheckedConfluenceIds } : {}),
        ...(formSelectedEntryModelIds.length > 0 ? { selectedEntryModelIds: formSelectedEntryModelIds } : {}),
      };
      setPresets((prev) => [...prev, newPreset]);
    }

    closeForm();
  }, [editingPresetId, formName, formStrategyId, formPlaybookId, formCheckedCriteriaIds, formCheckedConfluenceIds, formSelectedEntryModelIds, presets, closeForm]);

  // ---------------------------------------------------------------------------
  // Handlers — preset list actions
  // ---------------------------------------------------------------------------

  const handleDeletePreset = useCallback((id: string): void => {
    setPresets((prev) => prev.filter((p) => p.id !== id));
    // Close form if we're editing this preset
    setEditingPresetId((current) => {
      if (current === id) {
        setIsFormOpen(false);
        return undefined;
      }
      return current;
    });
  }, []);

  const handleMoveUp = useCallback((id: string): void => {
    setPresets((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((p) => p.id === id);
      if (idx <= 0) return prev;
      // Swap orders
      const current = sorted[idx];
      const above = sorted[idx - 1];
      if (!current || !above) return prev;
      const currentOrder = current.order;
      const aboveOrder = above.order;
      return prev.map((p) => {
        if (p.id === current.id) return { ...p, order: aboveOrder };
        if (p.id === above.id) return { ...p, order: currentOrder };
        return p;
      });
    });
  }, []);

  const handleMoveDown = useCallback((id: string): void => {
    setPresets((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((p) => p.id === id);
      if (idx < 0 || idx >= sorted.length - 1) return prev;
      const current = sorted[idx];
      const below = sorted[idx + 1];
      if (!current || !below) return prev;
      const currentOrder = current.order;
      const belowOrder = below.order;
      return prev.map((p) => {
        if (p.id === current.id) return { ...p, order: belowOrder };
        if (p.id === below.id) return { ...p, order: currentOrder };
        return p;
      });
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Handler — display tab
  // ---------------------------------------------------------------------------

  const handleCardInfoToggle = useCallback((field: CardInfoField): void => {
    setCardInfo((prev) =>
      prev.map((entry) =>
        entry.field === field ? { ...entry, show: !entry.show } : entry,
      ),
    );
  }, []);

  // ---------------------------------------------------------------------------
  // Handler — save modal
  // ---------------------------------------------------------------------------

  const handleSave = useCallback(async (): Promise<void> => {
    setSaveError(null);
    setIsSaving(true);
    try {
      await onSave({
        showQuickTrades,
        quickTradePresets: presets,
        activeTradesDefaultSort: activeSort,
        activeTradesCardInfo: cardInfo,
      });
      onClose();
    } catch {
      setSaveError('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [showQuickTrades, presets, activeSort, cardInfo, onSave, onClose]);

  // ---------------------------------------------------------------------------
  // Sorted presets for display
  // ---------------------------------------------------------------------------

  const sortedPresets = [...presets].sort((a, b) => a.order - b.order);

  // ---------------------------------------------------------------------------
  // Preset form JSX
  // ---------------------------------------------------------------------------

  const presetForm = (
    <div className="flex flex-col gap-3 border border-default-200 rounded-lg p-3 bg-default-50 mt-2">
      <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
        {editingPresetId !== undefined ? 'Edit Preset' : 'Add Preset'}
      </p>

      {/* Name */}
      <Input
        label="Name"
        size="sm"
        variant="bordered"
        isRequired
        value={formName}
        onValueChange={(v) => { setFormName(v); setPresetNameError(null); }}
        placeholder="e.g. OTE Retest, A+ Setup"
        {...(presetNameError !== null ? { isInvalid: true, errorMessage: presetNameError } : {})}
      />

      {/* Strategy */}
      <Select
        label="Strategy (optional)"
        size="sm"
        variant="bordered"
        selectedKeys={formStrategyId ? [formStrategyId] : []}
        onSelectionChange={(keys) => {
          if (keys === 'all') return;
          const key = Array.from(keys)[0] as string | undefined;
          handleFormStrategyChange(key ?? '');
        }}
      >
        {activeStrategies.map((s) => (
          <SelectItem key={s.id}>{s.name}</SelectItem>
        ))}
      </Select>

      {/* Playbook */}
      <Select
        label="Playbook (optional)"
        size="sm"
        variant="bordered"
        isDisabled={!formStrategyId}
        selectedKeys={formPlaybookId ? [formPlaybookId] : []}
        onSelectionChange={(keys) => {
          if (keys === 'all') return;
          const key = Array.from(keys)[0] as string | undefined;
          handleFormPlaybookChange(key ?? '');
        }}
      >
        {availablePlaybooks.map((p) => (
          <SelectItem key={p.id}>{p.name}</SelectItem>
        ))}
      </Select>

      {/* Playbook-dependent fields */}
      {formPlaybookId && (
        <>
          {/* Criteria */}
          {formCriteria.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <p className="text-xs text-text-secondary font-medium">Criteria to pre-check</p>
              <div className="flex flex-col gap-1 pl-1">
                {formCriteria.map((c) => (
                  <Checkbox
                    key={c.id}
                    size="sm"
                    isSelected={formCheckedCriteriaIds.includes(c.id)}
                    onValueChange={(checked) => {
                      setFormCheckedCriteriaIds((prev) =>
                        checked ? [...prev, c.id] : prev.filter((id) => id !== c.id),
                      );
                    }}
                  >
                    <span className="text-xs">{c.name}</span>
                  </Checkbox>
                ))}
              </div>
            </div>
          )}

          {/* Confluences */}
          {formConfluences.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <p className="text-xs text-text-secondary font-medium">Confluences to pre-mark</p>
              <div className="flex flex-col gap-1 pl-1">
                {formConfluences.map((c) => (
                  <Checkbox
                    key={c.id}
                    size="sm"
                    isSelected={formCheckedConfluenceIds.includes(c.id)}
                    onValueChange={(checked) => {
                      setFormCheckedConfluenceIds((prev) =>
                        checked ? [...prev, c.id] : prev.filter((id) => id !== c.id),
                      );
                    }}
                  >
                    <span className="text-xs">{c.name}</span>
                  </Checkbox>
                ))}
              </div>
            </div>
          )}

          {/* Entry Models */}
          {formEntryModels.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <p className="text-xs text-text-secondary font-medium">Entry models to pre-select</p>
              <div className="flex flex-col gap-1 pl-1">
                {formEntryModels.map((em) => (
                  <Checkbox
                    key={em.id}
                    size="sm"
                    isSelected={formSelectedEntryModelIds.includes(em.id)}
                    onValueChange={(checked) => {
                      setFormSelectedEntryModelIds((prev) =>
                        checked ? [...prev, em.id] : prev.filter((id) => id !== em.id),
                      );
                    }}
                  >
                    <span className="text-xs">{em.name}</span>
                  </Checkbox>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Form actions */}
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button size="sm" variant="light" onPress={closeForm}>
          Cancel
        </Button>
        <Button
          size="sm"
          color="primary"
          variant="solid"
          isDisabled={!formName.trim()}
          onPress={handleSavePreset}
        >
          {editingPresetId !== undefined ? 'Update Preset' : 'Add Preset'}
        </Button>
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Quick Trades tab JSX
  // ---------------------------------------------------------------------------

  const quickTradesTab = (
    <div className="flex flex-col gap-4">
      {/* Show Quick Trades toggle */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-medium text-text-primary">Show Quick Trades</p>
          <p className="text-xs text-text-tertiary">Display preset buttons in the Trades pane</p>
        </div>
        <Switch
          isSelected={showQuickTrades}
          onValueChange={setShowQuickTrades}
          size="sm"
          aria-label="Show Quick Trades"
        />
      </div>

      <Divider />

      {/* Preset list header */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-text-primary">Quick Trade Presets</p>
        {!isFormOpen && (
          <Button size="sm" variant="flat" color="primary" onPress={openAddForm}>
            + Add Preset
          </Button>
        )}
      </div>

      {/* Preset list */}
      {sortedPresets.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          {sortedPresets.map((preset, idx) => {
            const stratName = activeStrategies.find((s) => s.id === preset.strategyId)?.name;
            const pbName = preset.playbookId
              ? activePlaybooks.find((p) => p.id === preset.playbookId)?.name
              : undefined;

            return (
              <div
                key={preset.id}
                className="flex items-center gap-2 px-2 py-2 rounded-md border border-default-200 bg-default-50"
              >
                {/* Reorder buttons */}
                <div className="flex flex-col gap-0.5 shrink-0">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    isDisabled={idx === 0}
                    onPress={() => handleMoveUp(preset.id)}
                    aria-label="Move up"
                    className="h-4 w-4 min-w-0"
                  >
                    <ChevronUp size={10} />
                  </Button>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    isDisabled={idx === sortedPresets.length - 1}
                    onPress={() => handleMoveDown(preset.id)}
                    aria-label="Move down"
                    className="h-4 w-4 min-w-0"
                  >
                    <ChevronDown size={10} />
                  </Button>
                </div>

                {/* Preset info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{preset.name}</p>
                  {(stratName ?? pbName) && (
                    <p className="text-xs text-text-tertiary truncate">
                      {[stratName, pbName].filter(Boolean).join(' / ')}
                    </p>
                  )}
                </div>

                {/* Edit / Delete */}
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={() => openEditForm(preset)}
                    aria-label="Edit preset"
                  >
                    <Pencil size={12} />
                  </Button>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    color="danger"
                    onPress={() => handleDeletePreset(preset.id)}
                    aria-label="Delete preset"
                  >
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        !isFormOpen && (
          <p className="text-xs text-text-tertiary text-center py-2">
            No presets yet. Add one to enable Quick Trade buttons.
          </p>
        )
      )}

      {/* Inline preset form */}
      {isFormOpen && presetForm}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Display tab JSX
  // ---------------------------------------------------------------------------

  const displayTab = (
    <div className="flex flex-col gap-4">
      {/* Sort order */}
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-text-primary">Default Sort Order</p>
        <div className="flex flex-col gap-1.5 pl-1">
          {SORT_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={`flex items-center gap-2 text-sm cursor-pointer text-left py-0.5 ${
                activeSort === value ? 'text-primary font-medium' : 'text-text-primary'
              }`}
              onClick={() => setActiveSort(value)}
            >
              <span
                className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 flex items-center justify-center ${
                  activeSort === value ? 'border-primary' : 'border-default-400'
                }`}
              >
                {activeSort === value && (
                  <span className="w-1.5 h-1.5 rounded-full bg-primary block" />
                )}
              </span>
              {label}
            </button>
          ))}
        </div>
      </div>

      <Divider />

      {/* Card info fields */}
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-text-primary">Trade Card Info</p>
        <p className="text-xs text-text-tertiary">Choose which fields appear on each trade card</p>
        <div className="flex flex-col gap-1.5 pl-1">
          {CARD_INFO_FIELDS.map(({ field, label }) => {
            const entry = cardInfo.find((e) => e.field === field);
            const isShown = entry?.show ?? false;
            return (
              <Checkbox
                key={field}
                size="sm"
                isSelected={isShown}
                onValueChange={() => handleCardInfoToggle(field)}
              >
                <span className="text-sm">{label}</span>
              </Checkbox>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="top-center"
      scrollBehavior="inside"
      size={isDesktop ? 'md' : 'full'}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 pb-2">
          <span className="text-base font-semibold">Quick Trade Settings</span>
        </ModalHeader>

        <ModalBody className="gap-0 px-4 pb-2">
          {/* Tab switcher */}
          <div className="flex items-center gap-0.5 pb-3">
            {(['quick-trades', 'display'] as ActiveTab[]).map((tab) => (
              <Button
                key={tab}
                size="sm"
                variant={activeTab === tab ? 'flat' : 'light'}
                color={activeTab === tab ? 'primary' : 'default'}
                className="h-7 px-3 text-xs capitalize"
                onPress={() => setActiveTab(tab)}
              >
                {tab === 'quick-trades' ? 'Quick Trades' : 'Display'}
              </Button>
            ))}
          </div>

          {/* Tab content */}
          {activeTab === 'quick-trades' ? quickTradesTab : displayTab}
        </ModalBody>

        <ModalFooter className="flex flex-col gap-2 pt-3">
          {saveError !== null && (
            <p className="text-xs text-danger text-right w-full">{saveError}</p>
          )}
          <div className="flex items-center justify-between w-full">
            <Button variant="light" onPress={onClose} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button
              color="primary"
              isLoading={isSaving}
              isDisabled={isSaving}
              onPress={() => { void handleSave(); }}
            >
              Save
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
